/**
 * **Penghitung nomor pesanan per PERANGKAT** (§4b `design/OFFLINE-FIRST.md`).
 *
 * ⭐⭐⭐ Inilah bagian yang membuat nomor bill bisa lahir **tanpa jaringan dan tanpa wasit**:
 * prefiksnya `device_code` (unik per outlet, `device_code_uniq`), urutnya dihitung sendiri oleh
 * perangkat ini. Dua till yang sama-sama luring dan sama-sama menghitung dari satu tetap tak bisa
 * kembar — prefiksnya berbeda.
 *
 * ⛔ Menggantikan `UI/${Date.now().slice(-6)}/1`, yang berulang tiap ±16,7 menit sementara
 * `order_no_uniq` berlaku selamanya. Uraian lengkapnya di `@osbpos/contract` → `order-no.ts`.
 *
 * ### Kenapa `localStorage`, bukan memori
 * Layar kasir dimuat ulang, tabletnya di-restart malam hari, dan PWA-nya bisa ditutup di tengah
 * shift. Penghitung yang cuma hidup di memori kembali ke satu setiap kali — dan kembali ke satu
 * berarti **mengulang nomor yang sudah dicetak hari itu**.
 *
 * ⚠️ Alasan yang sama dengan `cache.ts` dan `outbox.ts`: `localStorage` di peramban modern bukan
 * SharedPreferences yang gampang hilang; ia bertahan selama data situsnya tak dibersihkan.
 */
import { formatOrderNo } from '@osbpos/contract';

const KEY = 'osbpos.orderSeq';

interface Simpanan {
  readonly device: string;
  readonly day: string;
  readonly seq: number;
}

/**
 * ⚠️ **Cadangan tanggal dari jam perangkat** — dipakai HANYA saat hari usaha belum diketahui layar
 * (pelayan sebelum `currentBusinessDate` tiba, kasir sebelum sesi kas terbaca).
 *
 * ⛔ Ini tak melemahkan keunikan: penghitungnya berkunci pada string tanggal yang SAMA dengan yang
 * dicetak, jadi apa pun tanggalnya, urutnya tak pernah diulang untuk tanggal itu. Yang bisa meleset
 * cuma **label harinya**, dan hanya di sekitar tengah malam.
 */
function hariCadangan(): string {
  const d = new Date();
  const p = (n: number): string => String(n).padStart(2, '0');
  return `${String(d.getFullYear())}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

function baca(): Simpanan | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw === null) return null;
    const v = JSON.parse(raw) as Partial<Simpanan>;
    if (typeof v.device !== 'string' || typeof v.day !== 'string' || typeof v.seq !== 'number') {
      return null;
    }
    return { device: v.device, day: v.day, seq: v.seq };
  } catch {
    return null;
  }
}

function tulis(v: Simpanan): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(v));
  } catch {
    /* ⚠️ Mode privat / kuota penuh. Nomornya tetap terbit; yang hilang cuma ingatannya. */
  }
}

/**
 * Ambil nomor berikutnya **dan naikkan penghitungnya**.
 *
 * ⚠️ Dinaikkan saat nomor DIAMBIL, bukan saat pesanannya berhasil dibuka. Percobaan yang gagal
 * membakar satu nomor ⇒ deretnya berlubang, dan itu SAH: celah di deretan cuma menimbulkan
 * pertanyaan, sementara nomor kembar menimbulkan dua bill yang tak bisa dibedakan di telinga tamu.
 * (⛔ Ini bukan nomor faktur pajak — di situ celah memang tak boleh.)
 */
export function takeOrderNo(deviceCode: string, businessDate: string): string {
  const day = businessDate === '' ? hariCadangan() : businessDate;
  const lama = baca();
  /* ⛔ Perangkat atau hari berganti ⇒ mulai dari satu. Perangkat ikut dibandingkan karena satu
     tablet bisa dipasangkan ulang jadi till lain, dan urut milik till lama tak berarti apa pun. */
  const seq = lama !== null && lama.device === deviceCode && lama.day === day ? lama.seq + 1 : 1;
  tulis({ device: deviceCode, day, seq });
  return formatOrderNo({ deviceCode, businessDate: day, seq });
}

/**
 * Terima saran urut BEBAS dari SERVER sesudah tabrakan (`ORDER-409-DUP_NO`).
 *
 * ⭐⭐ Ini jaring pengaman untuk satu keadaan yang nyata: **data situs dibersihkan di tengah hari**
 * (atau perangkat dipasang ulang) ⇒ penghitungnya kembali ke satu sementara nomor 1–120 sudah
 * dicetak. Tanpa jaring ini, kasir menabrak nomor kembar seratus kali berturut-turut, dan tiap
 * tabrakan adalah satu pesanan yang gagal dibuka **di depan tamu**.
 *
 * ⛔ Penghitung tak pernah MUNDUR: saran yang lebih rendah dari yang sudah dipakai diabaikan.
 */
export function adoptNextSeq(deviceCode: string, businessDate: string, nextSeq: number): void {
  /*
   * ⚠️ **`nextSeq` = urut yang BEBAS, bukan yang terakhir dipakai.** Server menghitungnya
   * `tertinggi + 1`, sedangkan yang disimpan di sini "terakhir dipakai" — jadi selisih satu itu
   * nyata, bukan gaya penulisan. Menyimpannya apa adanya membuat pengambilan berikutnya melompat
   * ke `nextSeq + 1` dan **melewati satu nomor tiap kali tabrakan** — celah yang lahir dari
   * perbaikan tabrakan, persis di deret nomor yang sedang kita rapikan.
   */
  const terakhir = nextSeq - 1;
  if (!Number.isInteger(terakhir) || terakhir < 0) return;
  const day = businessDate === '' ? hariCadangan() : businessDate;
  const lama = baca();
  const sekarang =
    lama !== null && lama.device === deviceCode && lama.day === day ? lama.seq : 0;
  if (terakhir <= sekarang) return;
  tulis({ device: deviceCode, day, seq: terakhir });
}

/** Hanya untuk uji. ⛔ Tak dipanggil kode produksi mana pun. */
export function resetOrderSeqForTest(): void {
  try { localStorage.removeItem(KEY); } catch { /* mode privat */ }
}
